// Maestro de bancos (2026-10-19): los bancos de Chile con su código SBIF/CMF y las instituciones que
// reciben transferencias (Coopeuch, Mercado Pago…). Cuentas, cheques y transferencias eligen el banco
// de esta lista en vez de escribirlo: así queda siempre igual escrito.
import { useQuery } from '@tanstack/react-query';
import type { KeyboardEvent } from 'react';
import { api } from '../api/cliente';

export interface Banco {
  codigo: string;
  nombre: string;
  razonSocial: string;
  marcas: string | null;
  tipo: 'BANCO' | 'ESTATAL' | 'EXTRANJERO' | 'OTRA';
}

export function useBancos() {
  return useQuery({
    queryKey: ['bancos'],
    queryFn: () => api.get<Banco[]>('/tesoreria/bancos'),
    staleTime: 60 * 60 * 1000,
  });
}

const GRUPOS: Array<[string, Array<Banco['tipo']>]> = [
  ['Bancos', ['ESTATAL', 'BANCO']],
  ['Bancos extranjeros', ['EXTRANJERO']],
  ['Otras instituciones', ['OTRA']],
];

/** Lista para elegir el banco; guarda el nombre corto. Un valor antiguo fuera del maestro se respeta. */
export function SelectorBanco({
  valor,
  alCambiar,
  etiqueta,
  vacio = 'Elegir banco…',
  deshabilitado,
  alTecla,
}: {
  valor: string;
  alCambiar: (nombre: string) => void;
  etiqueta: string;
  vacio?: string;
  deshabilitado?: boolean;
  alTecla?: (e: KeyboardEvent<HTMLSelectElement>) => void;
}) {
  const bancos = useBancos().data ?? [];
  const conocido = !valor || bancos.some((b) => b.nombre === valor);
  return (
    <select
      aria-label={etiqueta}
      value={valor}
      disabled={deshabilitado}
      onChange={(e) => alCambiar(e.target.value)}
      onKeyDown={alTecla}
    >
      <option value="">{vacio}</option>
      {!conocido && <option value={valor}>{valor}</option>}
      {GRUPOS.map(([titulo, tipos]) => {
        const lista = bancos.filter((b) => tipos.includes(b.tipo));
        return lista.length ? (
          <optgroup key={titulo} label={titulo}>
            {lista.map((b) => (
              <option
                key={b.codigo}
                value={b.nombre}
                title={[b.razonSocial, b.marcas, /^\d+$/.test(b.codigo) ? `código ${b.codigo}` : '']
                  .filter(Boolean)
                  .join(' · ')}
              >
                {b.nombre}
                {b.marcas ? ` (${b.marcas})` : ''}
              </option>
            ))}
          </optgroup>
        ) : null;
      })}
    </select>
  );
}
